<script lang="ts">
    import { VpReturnIcon } from '@decathlon-experience/icons/svelte';
    import { onMount } from 'svelte';

    import { ariaKeyDownA11yHandler } from '$common/utils/ariaKeyDownA11y';
    import { DefaultOption, DefaultOptions } from '$saga/types/search.types';
    import { filterOptions } from '$saga/utils/searchFilters';
    import CheckboxElement from '$saga/views/Search/common/CheckboxElement.svelte';
    import Button from '$saga/components/Button.svelte';
    import { setDepthPadding } from '$saga/utils/searchFilters';
    import NoResultsFilter from './NoResultsFilter.svelte';

    export let options: DefaultOptions;
    export let handleValidateSelection: () => void;
    export let values: string[];
    export let labels: string[];
    export let placeholder: string;
    export let allLabel: string;

    let inputElement: HTMLInputElement;
    let filteredOptions = options;
    let isUserSearching = false;
    // local values
    let selectedValue: string[] = values;
    let selectedLabel: string[] = labels;

    const handleSelection = (option: DefaultOption) => {
        // Special handling for "All" option
        if (option.value === 'all') {
            if (selectedValue.includes('all')) {
                // If "All" is already selected, do nothing
                selectedLabel = [];
                selectedValue = [];
                return;
            } else {
                // If selecting "All", remove all other options
                selectedLabel = [allLabel];
                selectedValue = ['all'];
                return;
            }
        }

        // If user selected a normal option, first remove "All" if it's selected
        if (selectedValue.includes('all')) {
            selectedLabel = selectedLabel.filter((item) => item !== allLabel);
            selectedValue = selectedValue.filter((item) => item !== 'all');
        }

        if (selectedValue.includes(option.value)) {
            selectedValue = selectedValue.filter(
                (value) => value !== option.value,
            );
            selectedLabel = selectedLabel.filter(
                (label) => label !== option.label,
            );
        } else {
            selectedValue = [...selectedValue, option.value];
            selectedLabel = [...selectedLabel, option.label];
        }
    };

    const onInputChange = (event: Event) => {
        const query = (event.target as HTMLInputElement).value;
        isUserSearching = query !== '';
        filteredOptions = filterOptions({ query, defaultOptions: options });
    };

    const resetSelection = () => {
        selectedValue = [];
        selectedLabel = [];
        values = selectedValue;
        labels = selectedLabel;
    };

    // remote validate the selection (e.g dialog is closed from outside click)
    export const validateSelection = () => {
        values = selectedValue;
        labels = selectedLabel;
        handleValidateSelection();
    };

    onMount(() => {
        if (inputElement) {
            inputElement.focus();
        }
    });
</script>

<div class="flex h-full w-full flex-col">
    <div class="no-scrollbar overflow-y-scroll pb-5xl">
        <!-- Input -->
        <div class="my-s flex">
            <input
                type="text"
                on:input={onInputChange}
                {placeholder}
                bind:this={inputElement}
            />
        </div>
        {#if selectedValue && selectedValue.length > 0}
            <span
                tabindex="0"
                class="py-2xs text-sm text-content-brand underline hover:no-underline"
                role="button"
                on:click={() => {
                    resetSelection();
                }}
                {...ariaKeyDownA11yHandler(() => {
                    resetSelection();
                })}
            >
                Tout effacer
            </span>
            {#each selectedValue as selectedOption, index}
                <CheckboxElement
                    onToggleCheck={() => {
                        handleSelection({
                            value: selectedOption,
                            label: selectedLabel[index],
                        });
                    }}
                    label={selectedLabel[index]}
                    isChecked={true}
                />
            {/each}
        {/if}

        {#if selectedValue.length > 0}
            <hr class="my-2xs h-px w-full bg-content-quiet" />
        {/if}

        <!-- Add "All" option -->
        <CheckboxElement
            onToggleCheck={() => {
                handleSelection({ value: 'all', label: allLabel });
            }}
            label={allLabel}
            labelClass={'mr-m font-semibold'}
            isChecked={selectedValue.includes('all')}
        />

        {#if filteredOptions && filteredOptions.length > 0}
            {#each filteredOptions as filteredOption}
                <CheckboxElement
                    containerClass={[
                        filteredOption.depth && !isUserSearching
                            ? setDepthPadding(filteredOption.depth)
                            : '',
                        filteredOption.depth === 0 && !isUserSearching
                            ? 'mt-m border-t border-solid border-border-quiet pt-xl font-semibold'
                            : '',
                    ]
                        .join(' ')
                        .trim()}
                    onToggleCheck={() => {
                        handleSelection(filteredOption);
                    }}
                    label={filteredOption.label}
                    isChecked={selectedValue.includes(filteredOption.value)}
                />
            {/each}
        {:else}
            <NoResultsFilter />
        {/if}
    </div>

    <div
        class="sticky bottom-0 left-0 mt-auto flex w-full justify-between border-t border-solid border-border-quiet bg-white py-s"
    >
        <Button
            size="small"
            intent="secondary"
            shape="rounded"
            type="button"
            on:click={() => {
                resetSelection();
                validateSelection();
            }}
            startIcon={VpReturnIcon}>Effacer</Button
        >
        <Button
            size="small"
            shape="rounded"
            type="button"
            on:click={() => validateSelection()}
        >
            Appliquer
        </Button>
    </div>
</div>

<style lang="postcss">
    input {
        appearance: unset;
        display: inline-block !important;
        margin: unset;
        padding: unset;
        max-width: unset;
        min-height: unset;
        outline: unset !important;
        background-color: inherit;
        font-family: inherit;
        font-size: inherit;
        color: inherit !important;
        border: unset;
        border-radius: unset;
        box-shadow: unset !important;
        transition: unset;
        @apply block w-full bg-container-quiet px-m py-xs leading-6 placeholder:text-rock-200;
    }
</style>
